import React, { useState, useEffect, createContext, useContext } from 'react';
import { Spinner } from 'react-bootstrap';
import i18n from '../utils/i18n';

interface I18nContextType {
  currentLanguage: string;
  isReady: boolean;
}

const I18nContext = createContext<I18nContextType>({
  currentLanguage: 'en',
  isReady: false
});

export const useI18nContext = () => useContext(I18nContext);

interface I18nWrapperProps {
  children: React.ReactNode;
}

const I18nWrapper: React.FC<I18nWrapperProps> = ({ children }) => {
  const [isI18nReady, setIsI18nReady] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const initializeI18n = async () => {
      // If i18n is already initialized, set ready immediately
      if (i18n.isInitialized) {
        setIsI18nReady(true);
        setCurrentLanguage(i18n.language || 'en');
        return;
      }

      // Wait for i18n to initialize with timeout
      const initPromise = new Promise<void>((resolve) => {
        const handleInitialized = () => {
          setIsI18nReady(true);
          setCurrentLanguage(i18n.language || 'en');
          resolve();
        };

        i18n.on('initialized', handleInitialized);
        
        // Cleanup function
        return () => {
          i18n.off('initialized', handleInitialized);
        };
      });

      // Timeout fallback - if i18n doesn't initialize in 5 seconds, proceed anyway
      const timeoutPromise = new Promise<void>((resolve) => {
        setTimeout(() => {
          setIsI18nReady(true);
          resolve();
        }, 5000);
      });

      await Promise.race([initPromise, timeoutPromise]);
    };

    // Listen for language changes globally
    const handleLanguageChanged = (lng: string) => {
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChanged);

    initializeI18n();

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  if (!isI18nReady) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading translations...</span>
          </Spinner>
          <div>Loading Quizter Club...</div>
        </div>
      </div>
    );
  }

  return (
    <I18nContext.Provider value={{ currentLanguage, isReady: isI18nReady }}>
      {children}
    </I18nContext.Provider>
  );
};

export default I18nWrapper; 